import type { SessionDetailDto } from '@bnn/shared';
import { useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { Alert, Badge, Button, Card, Field, Input, Modal, PageHeader, Spinner } from '../components/ui';
import { api } from '../lib/api';
import { dt, STATUS_LABEL, STATUS_TONE } from '../lib/format';
import { errMsg, useApi } from '../lib/useApi';
import { SessionReview } from './SessionReview';

type Action = 'review' | 'reopen-count' | 'complete' | 'cancel' | 'reopen';

export default function SessionDetailPage() {
  const { id = '' } = useParams();
  const { can } = useAuth();
  const nav = useNavigate();
  const s = useApi<SessionDetailDto>(`/sessions/${id}`);
  const [action, setAction] = useState<Action | null>(null);
  const [error, setError] = useState('');

  if (s.error) return <Alert>{s.error.message}</Alert>;
  if (!s.data) return <Spinner />;
  const d = s.data;
  const manage = can('sessions.manage');

  const simple = async (a: Action) => {
    setError('');
    try {
      s.setData(await api.post<SessionDetailDto>(`/sessions/${id}/${a}`, {}));
    } catch (e) {
      setError(errMsg(e));
    }
  };

  return (
    <>
      <Link to="/sessions" className="text-sm text-brand hover:underline">
        ← รอบการนับ
      </Link>
      <PageHeader
        title={d.session_code}
        subtitle={
          <>
            {d.warehouse_code} {d.warehouse_name} · สร้างโดย {d.created_by.display_name} {dt(d.created_at)}
            {d.note && <> · {d.note}</>}
          </>
        }
        actions={<Badge tone={STATUS_TONE[d.status]}>{STATUS_LABEL[d.status]}</Badge>}
      />
      {error && (
        <div className="mb-3">
          <Alert>{error}</Alert>
        </div>
      )}
      {d.status === 'CANCELLED' && <Alert tone="warn">ยกเลิกเมื่อ {dt(d.cancelled_at)} · เหตุผล: {d.cancel_reason}</Alert>}
      {d.complete_override_reason && <Alert tone="warn">ปิดรอบทั้งที่มีเครื่องรอ Sync · เหตุผล: {d.complete_override_reason}</Alert>}
      {d.pending_devices.length > 0 && (
        <Alert tone="warn">
          มี {d.pending_devices.length} เครื่องที่ยังมีรายการรอ Sync:{' '}
          {d.pending_devices.map((p) => `${p.label ?? p.device_id.slice(0, 8)} (${p.user ?? '-'}, ${p.pending_count} รายการ)`).join(', ')}
        </Alert>
      )}

      <Card className="my-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="mr-auto text-sm">
            <b>{d.item_count.toLocaleString()}</b> สินค้า · <b>{d.entry_count.toLocaleString()}</b> ครั้งที่บันทึก
            {d.new_barcode_count > 0 && (
              <>
                {' · '}
                <Link to={`/barcodes?source=SCAN&session_id=${d.id}`} className="text-brand underline">
                  Barcode ผูกใหม่ {d.new_barcode_count}
                </Link>
              </>
            )}
          </div>
          {d.status === 'OPEN' && can('count.scan') && (
            <Button variant="primary" onClick={() => nav(`/count/${d.id}`)}>
              ไปหน้านับ
            </Button>
          )}
          {manage && d.status === 'OPEN' && <Button onClick={() => void simple('review')}>ปิดรับนับ → ตรวจยอด</Button>}
          {manage && d.status === 'REVIEW' && (
            <>
              <Button onClick={() => void simple('reopen-count')}>กลับไปนับเพิ่ม</Button>
              <Button variant="primary" onClick={() => setAction('complete')}>
                ปิดรอบ (Complete)
              </Button>
            </>
          )}
          {manage && (d.status === 'OPEN' || d.status === 'REVIEW') && (
            <Button variant="danger" onClick={() => setAction('cancel')}>
              ยกเลิกรอบ
            </Button>
          )}
          {can('sessions.reopen') && d.status === 'COMPLETED' && <Button onClick={() => setAction('reopen')}>เปิดรอบใหม่ (Admin)</Button>}
        </div>
      </Card>

      {can('reports.view') && (
        <Card title="Export" className="mb-4">
          <div className="flex flex-wrap gap-2">
            {(
              [
                ['summary', 'xlsx', '⬇ Excel สรุปยอด (Summary)'],
                ['detail', 'xlsx', '⬇ Excel รายละเอียด (Detail)'],
                ['summary', 'csv', 'CSV สรุปยอด'],
                ['detail', 'csv', 'CSV รายละเอียด'],
              ] as const
            ).map(([kind, fmt, label]) => (
              <Button
                key={`${kind}-${fmt}`}
                size="sm"
                variant={fmt === 'xlsx' ? 'primary' : 'secondary'}
                onClick={() => void api.download(`/reports/${kind}?session_id=${d.id}&format=${fmt}`, `${d.session_code}_${kind}.${fmt}`).catch((e) => setError(errMsg(e)))}
              >
                {label}
              </Button>
            ))}
          </div>
          <p className="mt-2 text-xs text-muted">หัวคอลัมน์ภาษาอังกฤษ 1 แถว = 1 รายการ ใช้ต่อใน Excel / Power Query ได้ทันที · สรุปยอดไม่รวมรายการที่ยกเลิก · Excel สรุปยอดมีชีต ItemTotal (รวมเป็น Inventory UoM)</p>
        </Card>
      )}

      <SessionReview session={d} onChanged={s.reload} />

      <Modal open={!!action} title={action === 'complete' ? 'ปิดรอบการนับ' : action === 'cancel' ? 'ยกเลิกรอบการนับ' : 'เปิดรอบที่ปิดแล้ว'} onClose={() => setAction(null)}>
        {action && (
          <ReasonForm
            action={action}
            needsOverride={action === 'complete' && d.pending_devices.length > 0}
            onSubmit={async (reason) => {
              const body =
                action === 'complete' ? (d.pending_devices.length ? { override: true, override_reason: reason } : {}) : { reason };
              s.setData(await api.post<SessionDetailDto>(`/sessions/${id}/${action}`, body));
              setAction(null);
            }}
          >
            {action === 'complete' && <p className="text-sm">ปิดรอบแล้วแก้ไขยอดไม่ได้ (Admin เปิดใหม่ได้พร้อมเหตุผล)</p>}
            {action === 'cancel' && <p className="text-sm">รายการนับในรอบนี้ยังเก็บไว้ แต่จะไม่ถูกนับเป็นผล</p>}
          </ReasonForm>
        )}
      </Modal>
    </>
  );
}

function ReasonForm({
  action,
  needsOverride,
  onSubmit,
  children,
}: {
  action: Action;
  needsOverride: boolean;
  onSubmit: (reason: string) => Promise<void>;
  children?: ReactNode;
}) {
  const [reason, setReason] = useState('');
  const [ack, setAck] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const needReason = action !== 'complete' || needsOverride;

  return (
    <form
      className="space-y-3"
      onSubmit={async (e) => {
        e.preventDefault();
        if (needReason && reason.trim().length < 3) return setError('กรุณาระบุเหตุผล');
        if (needsOverride && !ack) return setError('กรุณายืนยันว่าทราบว่ามีเครื่องรอ Sync');
        setBusy(true);
        setError('');
        try {
          await onSubmit(reason.trim());
        } catch (err) {
          setError(errMsg(err));
        } finally {
          setBusy(false);
        }
      }}
    >
      {children}
      {error && <Alert>{error}</Alert>}
      {needsOverride && (
        <label className="flex items-start gap-2 text-sm">
          <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} className="mt-1" />
          ยังมีเครื่องที่มีรายการรอ Sync — รายการที่ส่งมาหลังปิดรอบจะถูกเก็บเป็น Sync Error ไม่รวมในยอด
        </label>
      )}
      {needReason && (
        <Field label="เหตุผล *">
          <Input value={reason} onChange={(e) => setReason(e.target.value)} autoFocus maxLength={500} />
        </Field>
      )}
      <Button type="submit" variant={action === 'cancel' ? 'danger' : 'primary'} className="w-full" loading={busy}>
        ยืนยัน
      </Button>
    </form>
  );
}
